import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface PromotionObject {
  id: number;
  titleEn: string;
  titleAr: string;
  promoCode: string;
  packageId: number;
  discountPercentage: number;
  allowedUsage: number;
  actualUsage;
  duration: number;
  isActive: boolean;
}

export interface PromotionListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
  promotions: Array<PromotionObject>;
  errors: unknown;
}
@Module
export default class PromotionsModule
  extends VuexModule
  implements PromotionListInfo
{
  promotions = [] as Array<PromotionObject>;
  users = [];
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  previous = 0;
  isNext = true;
  errors = {};

  /**
   * Get Ads list
   * @returns Array<AdsObject>
   */
  get promotionsList(): Array<PromotionObject> {
    return this.promotions;
  }

  /**
   * Get total items
   * @returns number
   */
  get promotionsListCount(): number {
    return this.totalItems;
  }

  @Mutation
  [Mutations.SET_PROMOTIONS](data) {
    this.promotions = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_PROMOTIONS_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.SET_PROMO_USERS](data) {
    this.users = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_PROMO_USERS_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Action
  [Actions.UPDATE_PROMO_USERS_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_PROMO_USERS_CURRENT_PAGE, val);
  }

  @Mutation
  [Mutations.RESET_PROMOTION_LIST]() {
    this.promotions = [] as Array<PromotionObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Action
  [Actions.RESET_PROMOTION_STORE]() {
    this.context.commit(Mutations.RESET_PROMOTION_LIST);
  }

  @Action
  [Actions.UPDATE_PROMOTIONS_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_PROMOTIONS_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_PROMOTION](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/promotions', id);
  }

  @Action
  [Actions.GET_PROMOTIONS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/promotions', {
      params: { page: this.currentPage },
    })
      .then(({ data }) => {
        data.data = data.data.map((r) => {
          r.createdAt = new Date(r.createdAt)
            .toLocaleString('en-GB')
            .split(',')[0];
          return r;
        });
        this.context.commit(Mutations.SET_PROMOTIONS, data);
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.message],
        // });
      });
  }

  @Action
  [Actions.CREATE_PROMOTION](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/promotions', data)
      .then(() => {
        this.context.commit(Mutations.RESET_PROMOTION_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }
  @Action
  [Actions.UPDATE_PROMOTION]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/promotions', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_PROMOTION_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.GET_PROMO_HISTORY](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/promotions/usage', id);
  }

  // @Action
  // [Actions.GET_PROMO_USERS](id) {
  //   ApiService.setAuthorizationHeader();
  //   return ApiService.query('/promotions/user-logs', {
  //     params: { page: this.currentPage, id: id },
  //   })
  //     .then(({ data }) => {
  //       data.data = data.data.map((r) => {
  //         r.createdAt = new Date(r.createdAt)
  //           .toLocaleString('en-GB')
  //           .split(',')[0];
  //         return r;
  //       });
  //       this.context.commit(Mutations.SET_PROMOTIONS, data);
  //     })
  //     .catch(({ response }) => {
  //       console.error(response);
  //       // this.context.commit(Mutations.SET_ERROR, {
  //       //   [response.statusText]: [response.data.message],
  //       // });
  //     });
  // }

  @Action
  [Actions.GET_PROMO_USERS]({ id, page = 1, limit = 10 }) {
    ApiService.setAuthorizationHeader();
    // id in path, page as query param(s)
    return ApiService.query(`/promotions/user-logs/${id}`, {
      params: { page, limit },
    })
      .then(({ data }) => {
        // format dates if you like
        data.data = data.data.map((r: any) => {
          if (r.createdAt) {
            r.createdAt = new Date(r.createdAt)
              .toLocaleString('en-GB')
              .split(',')[0];
          }
          return r;
        });
        this.context.commit(Mutations.SET_PROMO_USERS, data);
        return data;
      })
      .catch(({ response }) => {
        console.error(response);
        throw response;
      });
  }

  @Action
  [Actions.DELETE_PROMOTION](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/promotions', id)
      .then(() => {
        this.context.commit(Mutations.RESET_PROMOTION_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }
}
